import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { usePatient, useDeletePatient, useSettings, useInvoices, usePatientInvoiceSummary, useDiagnosticReceipts, usePatientReceiptSummary, usePatientCategories } from '../../services/httpServices/queries';
import { patientService } from '../../services/httpServices/patientService';
import { billingService } from '../../services/httpServices/billingService';
import { buildPatientPrintHtml, buildPatientCardHtml } from '../../utils/patientPrint';
import { joinFullName } from '../../utils/patientName';
import { patientCategoryLabel } from '../../utils/patientCategory';
import {
  composePatientAddress,
  permanentAddressParts,
  presentAddressParts,
  resolvePatientAddressNames,
} from '../../utils/patientAddress';
import { openPrintWindow } from '../../utils/printWindow';
import { usePermissions } from '../../hooks/usePermissions';
import { useBillingHistoryAccess } from '../../utils/billingAccess';
import {
  DIAGNOSTIC_PAYMENT_STATUS_OPTIONS,
  DIAGNOSTIC_PAYMENT_STATUS_META,
  diagnosticPaymentStatusFilter,
  diagnosticPaymentStatusOf,
  diagnosticReceiptStatusClass,
} from '../../utils/diagnosticReceiptStatus';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Pagination } from '../../components/ui/Pagination';
import {
  AlertCircle,
  ArrowLeft,
  Activity,
  Banknote,
  CalendarDays,
  Download,
  Droplets,
  Edit3,
  ExternalLink,
  FileText,
  HeartPulse,
  IdCard,
  ImageIcon,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Printer,
  QrCode,
  Receipt,
  ShieldAlert,
  ShieldCheck,
  Stethoscope,
  Trash2,
  UserRound,
  Wallet,
} from 'lucide-react';
import {
  Chip,
  DetailRow,
  EmptyState,
  InfoTile,
  ProfilePhoto,
  ProfileSkeleton,
  SectionCard,
  StatTile,
  StatusPill,
  TabBar,
  type ProfileTab,
} from '../../components/ui/profile-ui';

type PatientTab = 'overview' | 'medical' | 'insurance' | 'documents' | 'billing';

type BillingView = 'invoices' | 'receipts';

const PATIENT_TABS: readonly PatientTab[] = [
  'overview',
  'medical',
  'insurance',
  'documents',
  'billing',
];

const BILLING_VIEWS: readonly BillingView[] = ['invoices', 'receipts'];

const isPatientTab = (value: string | null): value is PatientTab =>
  PATIENT_TABS.includes((value ?? '') as PatientTab);

const isBillingView = (value: string | null): value is BillingView =>
  BILLING_VIEWS.includes((value ?? '') as BillingView);

// Status filter options for invoices. The list defaults to every status; `due`
// is the API's pseudo-status for any non-cancelled invoice with an outstanding
// balance (so it also covers partial and overdue invoices).
const ALL_INVOICE_STATUS = 'all';
const DEFAULT_INVOICE_STATUS = ALL_INVOICE_STATUS;

const INVOICE_STATUS_OPTIONS = [
  { value: ALL_INVOICE_STATUS, label: 'All statuses' },
  { value: 'due', label: 'Due' },
  { value: 'issued', label: 'Issued' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

const INVOICE_STATUS_ALIASES: Record<string, string> = {
  draft: 'due',
  partial: 'due',
  overdue: 'due',
};

/**
 * The receipts list has a single status filter: the derived paid / due state,
 * labelled "Status". The raw workflow status (ACTIVE / USED / ...) is still
 * shown per row but is not filterable here — the OPD receipt list is the place
 * for that, and two stacked dropdowns on a profile tab were easy to mix up.
 */
const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  ...DIAGNOSTIC_PAYMENT_STATUS_OPTIONS,
];

const invoiceStatusParam = (value: string | null) => {
  const normalized = String(value ?? '').toLowerCase();
  const aliased = INVOICE_STATUS_ALIASES[normalized] ?? normalized;
  return INVOICE_STATUS_OPTIONS.some((option) => option.value === aliased)
    ? aliased
    : DEFAULT_INVOICE_STATUS;
};

/** Invoice status shown in the UI: anything with an outstanding balance is Due. */
const invoiceDisplayStatus = (invoice: any): string => {
  const status = String(invoice?.status ?? '').toLowerCase();
  if (status === 'cancelled' || status === 'paid' || status === 'issued') return status;
  return Number(invoice?.dueAmount || 0) > 0 ? 'due' : 'paid';
};

/**
 * Status filter for receipts: `paid` / `due` — the same derived payment state
 * the OPD receipt list filters by, so both views agree on what "Due" means.
 */
const receiptStatusParam = (value: string | null) => diagnosticPaymentStatusFilter(value);

const BILLING_PAGE_SIZE = 10;

const formatMoney = (value?: number | null) =>
  `৳${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const invoiceStatusClass = (status?: string | null) => {
  switch (String(status ?? '').toLowerCase()) {
    case 'paid':
      return 'bg-success/10 text-success';
    case 'partial':
      return 'bg-primary/10 text-primary';
    case 'issued':
    case 'draft':
    case 'due':
      return 'bg-warning/10 text-warning';
    case 'overdue':
    case 'cancelled':
      return 'bg-danger/10 text-danger';
    default:
      return 'bg-text-muted/10 text-text-muted';
  }
};

const receiptStatusClass = diagnosticReceiptStatusClass;

/**
 * Workflow status text shown in the receipts table. Rendered in the same
 * upper-case form as the OPD receipt list so a receipt reads identically in
 * both places.
 */
const receiptStatusLabel = (status?: string | null) =>
  String(status ?? '').toUpperCase() || '—';

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

const titleCase = (value?: string | null) =>
  value
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '—';

const MEDICAL_FLAGS = [
  { key: 'drugAllergy', label: 'Drug Allergy', icon: ShieldAlert },
  { key: 'foodAllergy', label: 'Food Allergy', icon: ShieldAlert },
  { key: 'pregnancy', label: 'Pregnancy', icon: HeartPulse },
  { key: 'highRisk', label: 'High Risk', icon: Activity },
  { key: 'infectiousDisease', label: 'Infectious Disease', icon: Activity },
  { key: 'fallRisk', label: 'Fall Risk', icon: Activity },
  { key: 'wheelchair', label: 'Wheelchair', icon: Activity },
  { key: 'hearingImpairment', label: 'Hearing Impairment', icon: Activity },
  { key: 'visualImpairment', label: 'Visual Impairment', icon: Activity },
] as const;

export function PatientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePatient(id || '');
  const deletePatient = useDeletePatient();
  const { data: settingsData } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [invoicePage, setInvoicePage] = useState(1);
  const [receiptPage, setReceiptPage] = useState(1);
  const { hasPermission, hasAccess } = usePermissions();
  const { unrestricted: fullBillingHistory } = useBillingHistoryAccess();

  // The Billing tab is split across two APIs (hospital invoices and diagnostic
  // receipts), each guarded by its own permission, so a user who can only see
  // one of them still gets a useful tab instead of a 403. `hasAccess` covers
  // built-in roles (which gate by route rather than by permission codes).
  const canViewInvoices =
    hasAccess('/billing/invoices') ||
    hasPermission('billing:read') ||
    hasPermission('billing:invoice:read');
  const canViewReceipts =
    hasAccess('/opd/diagnostic-receipts') ||
    hasPermission('diagnostics:receipt:read');
  const canViewBilling = canViewInvoices || canViewReceipts;

  // The active tab lives in the URL (`?tab=billing`) so profile sections are
  // deep-linkable and survive a refresh or browser back/forward navigation.
  // Unknown values, and billing when the user has no billing permission, fall
  // back to the overview instead of rendering a broken section.
  const requestedTab = searchParams.get('tab');
  const tab: PatientTab =
    requestedTab === 'billing' && !canViewBilling
      ? 'overview'
      : isPatientTab(requestedTab)
        ? requestedTab
        : 'overview';

  const setTab = (next: PatientTab) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'overview') params.delete('tab');
        else params.set('tab', next);
        // Leaving Billing clears its sub-view and status filters so the URL
        // stays meaningful.
        if (next !== 'billing') {
          params.delete('view');
          params.delete('invoiceStatus');
          params.delete('receiptStatus');
          params.delete('receiptPaymentStatus');
        }
        return params;
      },
      { replace: true },
    );
  };

  // Secondary tabs inside Billing. Also URL-driven
  // (`?tab=billing&view=receipts`) so a specific record type is shareable.
  const viewParam = searchParams.get('view');
  const requestedView = isBillingView(viewParam) ? viewParam : null;
  const defaultBillingView: BillingView = canViewInvoices ? 'invoices' : 'receipts';
  const billingView: BillingView =
    requestedView === 'receipts' && canViewReceipts
      ? 'receipts'
      : requestedView === 'invoices' && canViewInvoices
        ? 'invoices'
        : defaultBillingView;

  const setBillingView = (next: BillingView) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', 'billing');
        params.set('view', next);
        return params;
      },
      { replace: true },
    );
  };

  // Status filters, also URL-driven (`?invoiceStatus=paid`,
  // `?receiptStatus=due`) so a filtered history can be shared. Receipts have a
  // single status filter here: the derived paid / due state.
  const invoiceStatus = invoiceStatusParam(searchParams.get('invoiceStatus'));
  const receiptStatus = receiptStatusParam(searchParams.get('receiptStatus'));

  const setStatusFilter = (key: 'invoiceStatus' | 'receiptStatus', value: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      },
      { replace: true },
    );
    if (key === 'invoiceStatus') setInvoicePage(1);
    else setReceiptPage(1);
  };

  const clearReceiptFilters = () => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('receiptStatus');
        return params;
      },
      { replace: true },
    );
    setReceiptPage(1);
  };

  const receiptFiltersActive = Boolean(receiptStatus);

  const billingTabActive = tab === 'billing';
  const billingEnabled = billingTabActive && Boolean(id);

  const { data: invoiceSummaryData } = usePatientInvoiceSummary(id, {
    enabled: billingEnabled && canViewInvoices,
  });
  const { data: receiptSummaryData } = usePatientReceiptSummary(id, {
    enabled: billingEnabled && canViewReceipts,
  });
  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    error: invoicesError,
  } = useInvoices(
    invoicePage,
    BILLING_PAGE_SIZE,
    {
      patientId: id,
      status: invoiceStatus === ALL_INVOICE_STATUS ? undefined : invoiceStatus,
    },
    { enabled: billingEnabled && canViewInvoices },
  );
  const {
    data: receiptsData,
    isLoading: receiptsLoading,
    error: receiptsError,
  } = useDiagnosticReceipts(
    receiptPage,
    BILLING_PAGE_SIZE,
    {
      patientId: id,
      paymentStatus: receiptStatus || undefined,
    },
    { enabled: billingEnabled && canViewReceipts },
  );

  const invoices = invoicesData?.data ?? [];
  const receipts = receiptsData?.data ?? [];
  const invoiceMeta = invoicesData?.meta;
  const receiptMeta = receiptsData?.meta;
  const invoiceSummary = invoiceSummaryData?.data;
  const receiptSummary = receiptSummaryData?.data;
  const totalBilled =
    Number(invoiceSummary?.billed || 0) + Number(receiptSummary?.billed || 0);
  const totalSpent =
    Number(invoiceSummary?.paid || 0) + Number(receiptSummary?.paid || 0);
  const totalDue =
    Number(invoiceSummary?.due || 0) + Number(receiptSummary?.due || 0);
  const billingDocCount =
    Number(invoiceSummary?.count || 0) + Number(receiptSummary?.count || 0);

  const patient = data?.data;

  // Patient categories are configured under Patients › Categories; the record
  // only stores the key, so resolve it to the configured label for display.
  const { data: categoriesData } = usePatientCategories();
  const categoryLabel = patientCategoryLabel(patient?.patientCategory, categoriesData?.data || []);

  const hospital = useMemo(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      nameBn: find('hospital_name_bn'),
      logo: find('hospital_logo'),
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      certified: find('hospital_certified'),
    };
  }, [settingsData]);

  const handlePrint = async () => {
    if (!patient) return;
    const printWindow = window.open('', '_blank', 'width=900,height=800');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the patient record');
      return;
    }
    // Legacy records store BD-API ids for division/district/upazila/union;
    // translate them so the printed profile shows readable names.
    printWindow.document.write(
      '<p style="font-family:Arial,sans-serif;padding:24px">Preparing patient record…</p>',
    );
    const printable = await resolvePatientAddressNames(patient);
    const html = buildPatientPrintHtml(printable, hospital);
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 350);
  };

  const handlePrintCard = () => {
    if (!patient) return;
    if (!openPrintWindow(buildPatientCardHtml(patient, hospital))) {
      toast.error('Please allow pop-ups to print');
    }
  };

  const handleDownloadPdf = () => {
    if (!id || !patient) return;
    try {
      // The endpoint returns the PDF with `Content-Disposition: attachment`, so
      // navigating to it starts a native browser download.
      const url = patientService.getPatientPdfUrl(id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `patient-${patient.registrationNumber}.pdf`;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Patient PDF downloaded');
    } catch {
      toast.error('Failed to download patient PDF');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deletePatient.mutateAsync(id);
      navigate('/patients');
    } catch {
      // handled by mutation toast/error
    }
  };

  const handleDownloadInvoicePdf = (invoice: any) => {
    try {
      // The endpoint returns the PDF with `Content-Disposition: attachment`, so
      // navigating to it starts a native browser download (same as the billing
      // invoice list).
      const url = billingService.getInvoicePdfUrl(invoice.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice PDF downloaded');
    } catch {
      toast.error('Failed to download invoice PDF');
    }
  };

  const openDiagnosticReceipt = (receipt: any) => {
    navigate(`/opd/diagnostic-receipts?search=${encodeURIComponent(receipt.receiptNumber)}`);
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (error || !patient) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-2xl border border-danger/20 bg-danger/5 p-8 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-danger/10 text-danger">
          <AlertCircle size={20} />
        </span>
        <p className="text-sm font-semibold text-danger">Failed to load patient details</p>
        <p className="max-w-sm text-xs text-text-muted">
          The patient may have been removed, or the server is unavailable. Please try again.
        </p>
        <button
          type="button"
          onClick={() => navigate('/patients')}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:border-primary/40"
        >
          <ArrowLeft size={14} />
          Back to Patients
        </button>
      </div>
    );
  }

  const fullName = joinFullName(patient);
  const ageLabel = patient.age ? `${patient.age} yrs` : '—';
  const medicalFlags = MEDICAL_FLAGS.filter((flag) => (patient as any)[flag.key]);
  const clinicalAlerts = patient.clinicalAlerts ?? [];
  const documentCount = (patient.photoUrl ? 1 : 0) + (patient.qrCodeUrl ? 1 : 0);

  const permanentAddress = composePatientAddress(permanentAddressParts(patient));

  const presentAddress = patient.sameAsPermanent
    ? 'Same as permanent'
    : composePatientAddress(presentAddressParts(patient));

  const tabs: ProfileTab<PatientTab>[] = [
    { id: 'overview', label: 'Overview', icon: UserRound },
    { id: 'medical', label: 'Medical', icon: HeartPulse, count: medicalFlags.length + clinicalAlerts.length },
    { id: 'insurance', label: 'Insurance', icon: ShieldCheck },
    { id: 'documents', label: 'Documents', icon: ImageIcon, count: documentCount },
    ...(canViewInvoices || canViewReceipts
      ? [
          {
            id: 'billing' as const,
            label: 'Billing',
            icon: Receipt,
            count: billingDocCount || undefined,
          },
        ]
      : []),
  ];

  return (
    <div className="w-full space-y-4">
      {/* Compact toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => navigate('/patients')}
            aria-label="Back to patients"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 bg-surface text-text-muted shadow-sm transition-colors hover:border-primary/40 hover:text-text"
          >
            <ArrowLeft size={17} />
          </button>
          <div>
            <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
              Clinical / Patients
            </p>
            <p className="text-sm font-semibold text-text">Patient profile</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrint}
            aria-label="Print patient record"
            title="Print patient record"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 bg-surface text-text-muted shadow-sm transition-colors hover:border-primary/40 hover:text-text"
          >
            <Printer size={16} />
          </button>
          <button
            type="button"
            onClick={handlePrintCard}
            aria-label="Print patient card"
            title="Print patient card"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 bg-surface text-text-muted shadow-sm transition-colors hover:border-primary/40 hover:text-text"
          >
            <IdCard size={16} />
          </button>
          <button
            type="button"
            onClick={handleDownloadPdf}
            aria-label="Download PDF"
            title="Download PDF"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 bg-surface text-text-muted shadow-sm transition-colors hover:border-primary/40 hover:text-text"
          >
            <Download size={16} />
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Delete patient"
            title="Delete patient"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-danger/20 bg-danger/5 text-danger transition-colors hover:bg-danger/10"
          >
            <Trash2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => navigate(`/patients/${id}/edit`)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-[0.8125rem] font-semibold text-white shadow-sm transition-colors hover:bg-primary-dark"
          >
            <Edit3 size={15} />
            Edit
          </button>
        </div>
      </div>

      {/* Profile hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border/70 bg-surface shadow-sm">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-1/3 h-56 w-56 rounded-full bg-secondary/10 blur-3xl" />

        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center">
          <div className="lg:order-2">
            <ProfilePhoto src={patient.photoUrl} name={fullName} active={patient.isActive} />
          </div>

          <div className="min-w-0 flex-1 space-y-3.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight text-text sm:text-2xl">{fullName}</h1>
                  <StatusPill active={patient.isActive} />
                </div>
                <p className="mt-0.5 text-[0.8125rem] text-text-muted">
                  {titleCase(patient.registrationType)}
                  <span className="text-text-muted"> · {categoryLabel}</span>
                </p>
                {clinicalAlerts.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {clinicalAlerts.slice(0, 4).map((alert) => (
                      <span
                        key={alert.id}
                        className="inline-flex items-center gap-1 rounded-md bg-warning/10 px-2 py-0.5 text-[0.6875rem] font-medium text-warning"
                      >
                        <ShieldAlert size={11} />
                        {alert.clinicalAlert?.label || alert.clinicalAlert?.name || 'Alert'}
                      </span>
                    ))}
                    {clinicalAlerts.length > 4 && (
                      <Chip tone="muted">+{clinicalAlerts.length - 4} more</Chip>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <InfoTile icon={IdCard} label="ID" value={patient.registrationNumber} mono />
              <InfoTile icon={Stethoscope} label="Category" value={categoryLabel} />
              <InfoTile icon={CalendarDays} label="Registered" value={formatDate(patient.registrationDate)} />
              <InfoTile
                icon={UserRound}
                label="Registered by"
                value={patient.createdByEmployee?.name || '—'}
              />
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[0.75rem] text-text-muted">
              {patient.mobileNumber && (
                <a
                  href={`tel:${patient.mobileNumber}`}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-text"
                >
                  <Phone size={13} />
                  {patient.mobileNumber}
                </a>
              )}
              {patient.email && (
                <a
                  href={`mailto:${patient.email}`}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-text"
                >
                  <Mail size={13} />
                  {patient.email}
                </a>
              )}
              {permanentAddress && (
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <MapPin size={13} className="shrink-0" />
                  <span className="truncate">{permanentAddress}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Clinical snapshot */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          icon={CalendarDays}
          label="Age"
          value={ageLabel}
          hint={patient.dateOfBirth ? formatDate(patient.dateOfBirth) : patient.estimatedAge ? 'Estimated age' : 'Date of birth not set'}
        />
        <StatTile
          icon={Droplets}
          label="Blood group"
          value={patient.bloodGroup || '—'}
          hint={titleCase(patient.gender)}
        />
        <StatTile
          icon={Activity}
          label="Medical flags"
          value={medicalFlags.length}
          hint={medicalFlags.length > 0 ? 'Needs attention' : 'No active flags'}
        />
        <StatTile
          icon={ShieldAlert}
          label="Clinical alerts"
          value={clinicalAlerts.length}
          hint={clinicalAlerts.length > 0 ? 'Active alerts' : 'None recorded'}
          tone={clinicalAlerts.length > 0 ? 'accent' : 'default'}
        />
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabBar tabs={tabs} active={tab} onChange={setTab} />
        <p className="hidden text-[0.6875rem] text-text-muted sm:block">
          Updated {formatDate(patient.updatedAt || patient.createdAt)}
        </p>
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard
            icon={UserRound}
            title="Personal information"
            subtitle="Demographics and identity"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="Full name" value={fullName} />
              <DetailRow label="Gender" value={titleCase(patient.gender)} />
              <DetailRow label="Husband / Father name" value={patient.localName || '—'} />
              <DetailRow
                label="Date of birth"
                value={
                  patient.dateOfBirth
                    ? `${formatDate(patient.dateOfBirth)}${patient.age ? ` (${patient.age} yrs)` : ''}`
                    : ageLabel
                }
              />
              <DetailRow label="Marital status" value={titleCase(patient.maritalStatus)} />
              <DetailRow label="Nationality" value={patient.nationality || '—'} />
              <DetailRow label="Occupation" value={patient.occupation || '—'} />
              <DetailRow label="Employer" value={patient.employer || '—'} />
            </div>
          </SectionCard>

          <SectionCard
            icon={Phone}
            title="Contact details"
            subtitle="Phone, email and addresses"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="Mobile" value={patient.mobileNumber || '—'} />
              <DetailRow label="Secondary mobile" value={patient.secondaryMobile || '—'} />
              <DetailRow label="Email" value={patient.email || '—'} />
              <DetailRow label="Present address" value={presentAddress} />
              <DetailRow
                label="Permanent address"
                value={permanentAddress}
                className="sm:col-span-2 xl:col-span-1"
              />
            </div>
          </SectionCard>

          <SectionCard
            icon={HeartPulse}
            title="Emergency & guardian"
            subtitle="Who to reach in an emergency"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="Emergency contact" value={patient.emergencyContactName || '—'} />
              <DetailRow label="Relation" value={titleCase(patient.emergencyContactRelation)} />
              <DetailRow label="Emergency phone" value={patient.emergencyContactPhone || '—'} />
              <DetailRow label="Alternate phone" value={patient.emergencyContactAltPhone || '—'} />
              <DetailRow label="Guardian name" value={patient.guardianName || '—'} />
              <DetailRow label="Guardian relation" value={titleCase(patient.guardianRelation)} />
              <DetailRow label="Guardian phone" value={patient.guardianPhone || '—'} />
              <DetailRow
                label="Guardian address"
                value={patient.guardianAddress || '—'}
                className="sm:col-span-2 xl:col-span-1"
              />
            </div>
          </SectionCard>

          <SectionCard
            icon={FileText}
            title="Registration"
            subtitle="Registration and category details"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="ID" value={patient.registrationNumber} mono />
              <DetailRow label="Registration type" value={titleCase(patient.registrationType)} />
              <DetailRow label="Category" value={categoryLabel} />
              <DetailRow label="Registered on" value={formatDate(patient.registrationDate)} />
              <DetailRow
                label="Registered by"
                value={
                  patient.createdByEmployee
                    ? `${patient.createdByEmployee.name}${
                        patient.createdByEmployee.employeeCode
                          ? ` (${patient.createdByEmployee.employeeCode})`
                          : ''
                      }`
                    : '—'
                }
              />
            </div>
          </SectionCard>
        </div>
      )}

      {tab === 'medical' && (
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard
            icon={Activity}
            title="Medical flags"
            subtitle="Active conditions that affect care"
          >
            {medicalFlags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {medicalFlags.map((flag) => {
                  const Icon = flag.icon;
                  return (
                    <span
                      key={flag.key}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-danger/20 bg-danger/10 px-2.5 py-1.5 text-[0.75rem] font-medium text-danger"
                    >
                      <Icon size={14} />
                      {flag.label}
                    </span>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                icon={Activity}
                title="No active medical flags"
                hint="Allergy, pregnancy and risk indicators will appear here."
              />
            )}

            {patient.medicalNote && (
              <div className="mt-4 rounded-xl border border-border/60 bg-background/60 p-3.5">
                <div className="mb-1.5 flex items-center gap-2">
                  <FileText size={14} className="text-text-muted" />
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-text-muted">
                    Medical note
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-[0.8125rem] text-text-muted">{patient.medicalNote}</p>
              </div>
            )}
          </SectionCard>

          <SectionCard
            icon={ShieldAlert}
            title="Clinical alerts"
            subtitle="Safety notices shown across the system"
          >
            {clinicalAlerts.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {clinicalAlerts.map((alert) => (
                  <span
                    key={alert.id}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-warning/20 bg-warning/10 px-2.5 py-1.5 text-[0.75rem] font-medium text-warning"
                  >
                    <ShieldAlert size={14} />
                    {alert.clinicalAlert?.label || alert.clinicalAlert?.name || 'Alert'}
                  </span>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={ShieldAlert}
                title="No clinical alerts"
                hint="Alerts assigned to this patient will appear here."
              />
            )}
          </SectionCard>
        </div>
      )}

      {tab === 'insurance' && (
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard
            icon={ShieldCheck}
            title="Insurance & corporate"
            subtitle="Coverage and authorization details"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="Insurance company" value={patient.insuranceCompany || '—'} />
              <DetailRow label="Policy number" value={patient.policyNumber || '—'} mono />
              <DetailRow label="Insurance card" value={patient.insuranceCardNumber || '—'} mono />
              <DetailRow label="Corporate org" value={patient.corporateOrg || '—'} />
              <DetailRow label="Member ID" value={patient.memberId || '—'} mono />
              <DetailRow label="Validity" value={formatDate(patient.insuranceValidity)} />
              <DetailRow
                label="Coverage"
                value={patient.coveragePercent != null ? `${patient.coveragePercent}%` : '—'}
              />
              <DetailRow
                label="Authorization no."
                value={patient.authorizationNumber || '—'}
                mono
                className="sm:col-span-2 xl:col-span-1"
              />
            </div>
          </SectionCard>

          <SectionCard
            icon={IdCard}
            title="Identity documents"
            subtitle="Government and travel identifiers"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="National ID" value={patient.nationalId || '—'} mono />
              <DetailRow label="Passport" value={patient.passport || '—'} mono />
              <DetailRow label="Birth certificate" value={patient.birthCertificate || '—'} mono />
              <DetailRow label="Driving license" value={patient.drivingLicense || '—'} mono />
            </div>
          </SectionCard>
        </div>
      )}

      {tab === 'documents' && (
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard
            icon={ImageIcon}
            title="Patient photo"
            subtitle="Click to open full size"
          >
            {patient.photoUrl ? (
              <a
                href={patient.photoUrl}
                target="_blank"
                rel="noreferrer"
                className="group relative block overflow-hidden rounded-xl border border-border/70 bg-background"
              >
                <img
                  src={patient.photoUrl}
                  alt={fullName}
                  className="max-h-[22rem] w-full object-contain transition duration-300 group-hover:scale-[1.01]"
                />
                <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent px-2.5 pb-2 pt-6 text-[0.6875rem] font-medium text-white">
                  Patient photo
                  <span className="inline-flex items-center gap-1">
                    <Download size={11} />
                    Open
                  </span>
                </span>
              </a>
            ) : (
              <EmptyState
                icon={ImageIcon}
                title="No photo uploaded"
                hint="A patient photo will appear here once uploaded."
              />
            )}
          </SectionCard>

          <SectionCard
            icon={QrCode}
            title="Patient QR code"
            subtitle="Scan to open this patient record"
          >
            {patient.qrCodeUrl ? (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-border/70 bg-white p-4">
                <img src={patient.qrCodeUrl} alt="Patient QR code" className="h-48 w-48 object-contain" />
                <p className="font-mono text-[0.6875rem] text-text-muted">{patient.registrationNumber}</p>
              </div>
            ) : (
              <EmptyState
                icon={QrCode}
                title="No QR code available"
                hint="A QR code is generated with the patient record."
              />
            )}
          </SectionCard>
        </div>
      )}

      {tab === 'billing' && (
        <div className="space-y-4">
          {/* Money snapshot across invoices + diagnostic receipts */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              icon={Wallet}
              label="Total spent"
              value={formatMoney(totalSpent)}
              hint={fullBillingHistory ? 'All time' : 'Invoices limited to last 7 days'}
              tone="primary"
            />
            <StatTile
              icon={Receipt}
              label="Total billed"
              value={formatMoney(totalBilled)}
              hint={`${billingDocCount} document${billingDocCount === 1 ? '' : 's'}`}
            />
            <StatTile
              icon={Banknote}
              label="Outstanding"
              value={formatMoney(totalDue)}
              hint={totalDue > 0 ? 'Balance still due' : 'Fully settled'}
              tone={totalDue > 0 ? 'accent' : 'default'}
            />
            <StatTile
              icon={FileText}
              label="Billing records"
              value={billingDocCount}
              hint={`${Number(invoiceSummary?.count || 0)} invoice${
                Number(invoiceSummary?.count || 0) === 1 ? '' : 's'
              } · ${Number(receiptSummary?.count || 0)} receipt${
                Number(receiptSummary?.count || 0) === 1 ? '' : 's'
              }`}
            />
          </div>

          {/* Record type tabs */}
          {canViewInvoices && canViewReceipts && (
            <div
              role="tablist"
              aria-label="Billing records"
              className="inline-flex w-full items-center gap-1 rounded-xl border border-border/70 bg-surface p-1 shadow-sm sm:w-auto"
            >
              {[
                {
                  id: 'invoices' as const,
                  label: 'Invoices',
                  icon: FileText,
                  count: Number(invoiceSummary?.count || 0),
                },
                {
                  id: 'receipts' as const,
                  label: 'Receipts',
                  icon: Receipt,
                  count: Number(receiptSummary?.count || 0),
                },
              ].map((item) => {
                const Icon = item.icon;
                const active = billingView === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setBillingView(item.id)}
                    className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-[0.8125rem] font-medium transition-all sm:flex-none ${
                      active
                        ? 'bg-primary/10 text-primary shadow-sm ring-1 ring-inset ring-primary/15'
                        : 'text-text-muted hover:bg-background hover:text-text'
                    }`}
                  >
                    <Icon
                      size={14}
                      className={active ? 'text-primary' : 'text-text-muted'}
                    />
                    {item.label}
                    <span
                      className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[0.625rem] font-semibold tabular-nums ${
                        active
                          ? 'bg-primary/15 text-primary'
                          : 'bg-background text-text-muted'
                      }`}
                    >
                      {item.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {canViewInvoices && billingView === 'invoices' && (
            <SectionCard
              icon={FileText}
              title="Invoices"
              subtitle="Hospital services billed to this patient"
              action={
                <div className="flex items-center gap-2">
                  <span className="text-[0.6875rem] text-text-muted">
                    {invoiceMeta?.total ?? Number(invoiceSummary?.count || 0)} total
                  </span>
                  <select
                    value={invoiceStatus}
                    onChange={(event) =>
                      setStatusFilter('invoiceStatus', event.target.value)
                    }
                    aria-label="Filter invoices by status"
                    className="rounded-lg border border-border bg-background px-2 py-1 text-[0.6875rem] font-medium text-text focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    {INVOICE_STATUS_OPTIONS.map((option) => (
                      <option key={option.value || 'all'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              }
            >
              {invoicesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={22} className="animate-spin text-primary" />
                </div>
              ) : invoicesError ? (
                <div className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/5 px-3 py-2.5 text-[0.75rem] text-danger">
                  <AlertCircle size={15} />
                  Failed to load invoices
                </div>
              ) : invoices.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title={
                    invoiceStatus !== ALL_INVOICE_STATUS
                      ? 'No invoices match this status'
                      : 'No invoices yet'
                  }
                  hint={
                    invoiceStatus !== ALL_INVOICE_STATUS
                      ? 'Try a different status filter.'
                      : 'Bills raised for this patient will appear here.'
                  }
                />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-[0.75rem]">
                      <thead>
                        <tr className="border-b border-border/60 text-left text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                          <th className="pb-2 pr-3">Invoice</th>
                          <th className="pb-2 pr-3">Date</th>
                          <th className="pb-2 pr-3 text-right">Total</th>
                          <th className="pb-2 pr-3 text-right">Paid</th>
                          <th className="pb-2 pr-3 text-right">Due</th>
                          <th className="pb-2 pr-3">Status</th>
                          <th className="pb-2 text-right">PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice: any) => (
                          <tr
                            key={invoice.id}
                            className="border-b border-border/50 last:border-0"
                          >
                            <td className="whitespace-nowrap py-2.5 pr-3 font-mono font-medium text-text">
                              {invoice.invoiceNumber}
                            </td>
                            <td className="whitespace-nowrap py-2.5 pr-3 text-text-muted">
                              {formatDate(invoice.createdAt)}
                            </td>
                            <td className="py-2.5 pr-3 text-right tabular-nums text-text">
                              {formatMoney(invoice.total)}
                            </td>
                            <td className="py-2.5 pr-3 text-right tabular-nums text-success">
                              {formatMoney(invoice.paidAmount)}
                            </td>
                            <td
                              className={`py-2.5 pr-3 text-right tabular-nums ${
                                Number(invoice.dueAmount) > 0
                                  ? 'font-medium text-danger'
                                  : 'text-text-muted'
                              }`}
                            >
                              {formatMoney(invoice.dueAmount)}
                            </td>
                            <td className="py-2.5 pr-3">
                              <span
                                className={`inline-flex rounded-md px-1.5 py-0.5 text-[0.625rem] font-semibold capitalize ${invoiceStatusClass(invoiceDisplayStatus(invoice))}`}
                              >
                                {invoiceDisplayStatus(invoice)}
                              </span>
                            </td>
                            <td className="py-2.5 text-right">
                              <button
                                type="button"
                                onClick={() => handleDownloadInvoicePdf(invoice)}
                                title="Download invoice PDF"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/70 text-text-muted transition-colors hover:border-primary/40 hover:text-primary"
                              >
                                <Download size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {invoiceMeta && invoiceMeta.totalPages > 1 && (
                    <div className="-mx-4 -mb-4 mt-3 sm:-mx-5">
                      <Pagination
                        page={invoicePage}
                        totalPages={invoiceMeta.totalPages}
                        total={invoiceMeta.total}
                        limit={BILLING_PAGE_SIZE}
                        onPageChange={setInvoicePage}
                        itemName="invoices"
                      />
                    </div>
                  )}
                </>
              )}
            </SectionCard>
          )}

          {canViewReceipts && billingView === 'receipts' && (
            <SectionCard
              icon={Receipt}
              title="Diagnostic receipts"
              subtitle="Laboratory and imaging receipts for this patient"
              action={
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <span className="text-[0.6875rem] text-text-muted">
                    {receiptMeta?.total ?? Number(receiptSummary?.count || 0)} total
                  </span>
                  <label className="flex items-center gap-1.5 text-[0.6875rem] font-medium text-text-muted">
                    Status
                    <select
                      value={receiptStatus}
                      onChange={(event) =>
                        setStatusFilter('receiptStatus', event.target.value)
                      }
                      aria-label="Filter receipts by status"
                      title="Filter by paid / due status"
                      className="rounded-lg border border-border bg-background px-2 py-1 text-[0.6875rem] font-medium text-text focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value || 'all'} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {receiptFiltersActive && (
                    <button
                      type="button"
                      onClick={clearReceiptFilters}
                      className="rounded-lg border border-border px-2 py-1 text-[0.6875rem] font-medium text-text-muted transition-colors hover:border-danger/40 hover:text-danger"
                    >
                      Clear
                    </button>
                  )}
                </div>
              }
            >
              {receiptsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={22} className="animate-spin text-primary" />
                </div>
              ) : receiptsError ? (
                <div className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/5 px-3 py-2.5 text-[0.75rem] text-danger">
                  <AlertCircle size={15} />
                  Failed to load receipts
                </div>
              ) : receipts.length === 0 ? (
                <EmptyState
                  icon={Receipt}
                  title={
                    receiptFiltersActive
                      ? 'No receipts match this status'
                      : 'No receipts yet'
                  }
                  hint={
                    receiptFiltersActive
                      ? 'Try a different status filter.'
                      : 'Lab and imaging receipts for this patient will appear here.'
                  }
                />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-[0.75rem]">
                      <thead>
                        <tr className="border-b border-border/60 text-left text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                          <th className="pb-2 pr-3">Receipt</th>
                          <th className="pb-2 pr-3">Date</th>
                          <th className="pb-2 pr-3 text-right">Tests</th>
                          <th className="pb-2 pr-3 text-right">Total</th>
                          <th className="pb-2 pr-3 text-right">Paid</th>
                          <th className="pb-2 pr-3 text-right">Due</th>
                          <th className="pb-2 pr-3">Payment</th>
                          <th className="pb-2 pr-3">Status</th>
                          <th className="pb-2 text-right">Open</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receipts.map((receipt: any) => {
                          // Same derivations as the OPD receipt list: Tests
                          // counts real tests (not accessories), Total is what
                          // was charged (Paid + Due) and Payment is derived from
                          // Paid + Due so both views read identically.
                          const paid = Number(receipt.paidAmount || 0);
                          const due = Number(receipt.dueAmount || 0);
                          const total = paid + due;
                          const paymentStatus = diagnosticPaymentStatusOf(receipt);
                          const paymentMeta =
                            DIAGNOSTIC_PAYMENT_STATUS_META[paymentStatus];
                          const testCount = (receipt.items ?? []).filter(
                            (item: any) => !item.isAccessory,
                          ).length;
                          return (
                            <tr
                              key={receipt.id}
                              className="border-b border-border/50 last:border-0"
                            >
                              <td className="whitespace-nowrap py-2.5 pr-3 font-mono font-medium text-text">
                                {receipt.receiptNumber}
                              </td>
                              <td className="whitespace-nowrap py-2.5 pr-3 text-text-muted">
                                {formatDate(receipt.createdAt)}
                              </td>
                              <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">
                                {testCount}
                              </td>
                              <td className="py-2.5 pr-3 text-right tabular-nums text-text">
                                {formatMoney(total)}
                              </td>
                              <td className="py-2.5 pr-3 text-right tabular-nums text-success">
                                {formatMoney(paid)}
                              </td>
                              <td
                                className={`py-2.5 pr-3 text-right tabular-nums ${
                                  due > 0 ? 'font-medium text-danger' : 'text-text-muted'
                                }`}
                              >
                                {formatMoney(due)}
                              </td>
                              <td className="py-2.5 pr-3">
                                <span
                                  className={`inline-flex rounded-md px-1.5 py-0.5 text-[0.625rem] font-semibold ${paymentMeta.className}`}
                                  title={`Payment ${paymentMeta.label}`}
                                >
                                  {paymentMeta.label}
                                </span>
                              </td>
                              <td className="py-2.5 pr-3">
                                <span
                                  className={`inline-flex rounded-md px-1.5 py-0.5 text-[0.625rem] font-semibold ${receiptStatusClass(receipt.status)}`}
                                >
                                  {receiptStatusLabel(receipt.status)}
                                </span>
                              </td>
                              <td className="py-2.5 text-right">
                                <button
                                  type="button"
                                  onClick={() => openDiagnosticReceipt(receipt)}
                                  title="Open receipt in Diagnostic Receipts"
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/70 text-text-muted transition-colors hover:border-primary/40 hover:text-primary"
                                >
                                  <ExternalLink size={13} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {receiptMeta && receiptMeta.totalPages > 1 && (
                    <div className="-mx-4 -mb-4 mt-3 sm:-mx-5">
                      <Pagination
                        page={receiptPage}
                        totalPages={receiptMeta.totalPages}
                        total={receiptMeta.total}
                        limit={BILLING_PAGE_SIZE}
                        onPageChange={setReceiptPage}
                        itemName="receipts"
                      />
                    </div>
                  )}
                </>
              )}
            </SectionCard>
          )}
        </div>
      )}

      {/* Modals */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete patient?"
        description={
          <>
            Are you sure you want to delete <strong className="text-text">{fullName}</strong>? This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete patient"
        variant="danger"
        loading={deletePatient.isPending}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
